import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { AlertCircle, Target, Filter, TrendingUp, Hash } from 'lucide-react';
import { useFarmingResultsSelection } from '../../context/FarmingResultsSelectionContext';
import { 
  fetchActiveNegatives, 
  ActiveNegativesSummary 
} from '../../services/activeNegativesService';

export const ActiveNegativesDashboard: React.FC = () => {
  const { selectedCompany, selectedProduct } = useFarmingResultsSelection();
  const [data, setData] = useState<ActiveNegativesSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!selectedCompany || !selectedProduct) return;

    const asin = selectedProduct.id || selectedProduct.ASIN;
    if (!asin) {
      setError('No ASIN found for selected product');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log(`Fetching Active Negatives data for: ${selectedCompany}/${asin}`);
      
      const result = await fetchActiveNegatives(selectedCompany, asin);
      setData(result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch Active Negatives data';
      console.error('Error fetching Active Negatives:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [selectedCompany, selectedProduct]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!selectedCompany || !selectedProduct) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center max-w-md">
          <Target className="h-12 w-12 text-app-primary mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Select Company & ASIN
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Choose a company and ASIN from the sidebar to view Active Negatives data.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-app-primary mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading Active Negatives...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Error Loading Data
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {error}
          </p>
          <Button onClick={fetchData} variant="outline">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center text-gray-500 dark:text-gray-400 py-8">
        No data available
      </div>
    );
  }

  // Calculate max rows for display
  const maxRows = Math.max(
    data.convertedTerms.length,
    data.negativeExacts.length,
    data.negativePhrases.length,
    data.brandNamesTerms.length
  );

  return (
    <div className="space-y-6">
      {/* Farming Group Display */}
      {data.farmingGroup && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Farming Group
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-semibold text-app-primary">
              {data.farmingGroup}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Existing Metrics */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Total Converted Terms
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-app-primary">
              {data.totalConvertedTerms.toLocaleString()}
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Terms with sales</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Total Brand Names
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-app-primary">
              {data.totalBrandNamesTerms.toLocaleString()}
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Brand names to neg out</p>
          </CardContent>
        </Card>

        {/* New Negative Exacts Metrics */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
              <Hash className="h-4 w-4" />
              Total Negative Exacts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {data.totalNegativeExacts.toLocaleString()}
            </div>
            <div className="text-lg font-semibold text-blue-500 dark:text-blue-300 mt-1">
              {data.totalNegativeExactsClicks.toLocaleString()} clicks
            </div>
          </CardContent>
        </Card>

        {/* New Negative Phrases Metrics */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Total Negative Phrases
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
              {data.totalNegativePhrases.toLocaleString()}
            </div>
            <div className="text-lg font-semibold text-purple-500 dark:text-purple-300 mt-1">
              {data.totalNegativePhrasesClicks.toLocaleString()} clicks
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Data Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-app-primary" />
            Active Negatives
          </CardTitle>
          <CardDescription>
            Comprehensive view of converted terms, negative exacts, negative phrases, and brand names (limited to 190 entries per column)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {maxRows === 0 ? (
            <div className="text-center py-8">
              <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">No active negatives found</p>
            </div>
          ) : (
            <div className="w-full">
              <table className="w-full table-fixed border-collapse">
                <colgroup>
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '7%' }} />
                  <col style={{ width: '0.5%' }} />
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '7%' }} />
                  <col style={{ width: '7%' }} />
                  <col style={{ width: '0.5%' }} />
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '7%' }} />
                  <col style={{ width: '7%' }} />
                  <col style={{ width: '0.5%' }} />
                  <col style={{ width: '9.5%' }} />
                </colgroup>
                <thead>
                  <tr className="border-b dark:border-gray-700">
                    <th className="py-3 px-2 text-left font-semibold text-gray-900 dark:text-gray-100 text-sm">
                      Converted Terms
                    </th>
                    <th className="py-3 px-2 text-right font-semibold text-gray-900 dark:text-gray-100 text-sm">
                      Sales
                    </th>
                    <th className="py-3 px-1">
                      <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-auto"></div>
                    </th>
                    <th className="py-3 px-2 text-left font-semibold text-blue-900 dark:text-blue-100 text-sm">
                      Negative Exacts
                    </th>
                    <th className="py-3 px-2 text-right font-semibold text-blue-900 dark:text-blue-100 text-sm">
                      Clicks
                    </th>
                    <th className="py-3 px-2 text-right font-semibold text-blue-900 dark:text-blue-100 text-sm">
                      Probability
                    </th>
                    <th className="py-3 px-1">
                      <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-auto"></div>
                    </th>
                    <th className="py-3 px-2 text-left font-semibold text-purple-900 dark:text-purple-100 text-sm">
                      Negative Phrases
                    </th>
                    <th className="py-3 px-2 text-right font-semibold text-purple-900 dark:text-purple-100 text-sm">
                      Clicks
                    </th>
                    <th className="py-3 px-2 text-right font-semibold text-purple-900 dark:text-purple-100 text-sm">
                      Probability
                    </th>
                    <th className="py-3 px-1">
                      <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-auto"></div>
                    </th>
                    <th className="py-3 px-2 text-left font-semibold text-gray-900 dark:text-gray-100 text-sm">
                      <div>Negative Phrases</div>
                      <div>from Brand Names</div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: maxRows }).map((_, index) => {
                    const convertedTerm = data.convertedTerms[index];
                    const negativeExact = data.negativeExacts[index];
                    const negativePhrase = data.negativePhrases[index];
                    const brandName = data.brandNamesTerms[index];
                    
                    return (
                      <tr
                        key={index}
                        className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                      >
                        <td className="py-3 px-2 text-sm">
                          <div className="font-medium text-gray-900 dark:text-gray-100 truncate" title={convertedTerm?.term}>
                            {convertedTerm?.term || '-'}
                          </div>
                        </td>
                        <td className="py-3 px-2 text-right text-gray-900 dark:text-gray-100 text-sm">
                          {convertedTerm ? `$${Math.round(convertedTerm.sales).toLocaleString()}` : '-'}
                        </td>
                        <td className="py-3 px-1">
                          <div className="w-px h-8 bg-gray-300 dark:bg-gray-600 mx-auto"></div>
                        </td>
                        <td className="py-3 px-2 text-sm">
                          <div className="font-medium text-blue-900 dark:text-blue-100 truncate" title={negativeExact?.target}>
                            {negativeExact?.target || '-'}
                          </div>
                        </td>
                        <td className="py-3 px-2 text-right text-blue-900 dark:text-blue-100 text-sm">
                          {negativeExact ? negativeExact.clicks.toLocaleString() : '-'}
                        </td>
                        <td className="py-3 px-2 text-right text-blue-900 dark:text-blue-100 text-sm">
                          {negativeExact?.probability !== undefined
                            ? `${negativeExact.probability.toFixed(1)}%`
                            : '-'
                          }
                        </td>
                        <td className="py-3 px-1">
                          <div className="w-px h-8 bg-gray-300 dark:bg-gray-600 mx-auto"></div>
                        </td>
                        <td className="py-3 px-2 text-sm">
                          <div className="font-medium text-purple-900 dark:text-purple-100 truncate" title={negativePhrase?.target}>
                            {negativePhrase?.target || '-'}
                          </div>
                        </td>
                        <td className="py-3 px-2 text-right text-purple-900 dark:text-purple-100 text-sm">
                          {negativePhrase ? negativePhrase.clicks.toLocaleString() : '-'}
                        </td>
                        <td className="py-3 px-2 text-right text-purple-900 dark:text-purple-100 text-sm">
                          {negativePhrase?.probability !== undefined
                            ? `${negativePhrase.probability.toFixed(1)}%`
                            : '-'
                          }
                        </td>
                        <td className="py-3 px-1">
                          <div className="w-px h-8 bg-gray-300 dark:bg-gray-600 mx-auto"></div>
                        </td>
                        <td className="py-3 px-2 text-sm">
                          <div className="font-medium text-gray-900 dark:text-gray-100 truncate" title={brandName}>
                            {brandName || '-'}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ActiveNegativesDashboard;